import {ChangeEvent, Dispatch, SetStateAction, useCallback, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useAppDispatch} from '@lib/store';
import {AppThunkApiConfig, LoadingStatus, withAsyncThunkErrorHandling} from '@lib/store';
import {findByEmail, useDebounce} from '@lib/utils';
import {AsyncThunkAction} from '@reduxjs/toolkit';
import {MAX_GROUP_MEMBERS} from '@lib/store/src/data/groups/constants';
import {ChipSelect} from '@molecules';
import {GroupMemberSelectorItem} from './GroupMemberSelectorItem';
import {GroupMemberSelectorEmptyState} from './GroupMemberSelectorEmptyState';
import {PersonLike} from '@lib/infrastructure';

type Props<T extends PersonLike> = {
  value: T[];
  setValue: Dispatch<SetStateAction<T[]>>;
  searchResults: T[];
  searchStatus: LoadingStatus;
  onSearch: (searchCriteria: string) => AsyncThunkAction<T[], string, AppThunkApiConfig<unknown>>;
  disabledValues?: {id: string; reason: string}[];
};

export const GroupMemberSelector = <T extends PersonLike>({
  value,
  setValue,
  onSearch,
  searchResults,
  searchStatus,
  disabledValues,
}: Props<T>) => {
  const dispatch = useAppDispatch();
  const {t} = useTranslation();
  const [searchCriteria, setSearchCriteria] = useState('');
  const [updatingSearchCriteria, setUpdatingSearchCriteria] = useState(false);
  const debouncedSearchCriteria = useDebounce(searchCriteria, 300);

  const nonSelectedSearchResults = useMemo(
    () => searchResults.filter((searchResult) => !findByEmail(value, searchResult.email)),
    [searchResults, value],
  );

  const isMaximumNumberOfGroupMemberReached = value.length >= MAX_GROUP_MEMBERS;

  const actualSearchResults = useMemo(() => {
    if (isMaximumNumberOfGroupMemberReached || !searchCriteria.length) {
      return [];
    }
    return nonSelectedSearchResults;
  }, [isMaximumNumberOfGroupMemberReached, searchCriteria.length, nonSelectedSearchResults]);

  const updateSearchCriteria = useCallback((criteria: string) => {
    setUpdatingSearchCriteria(true);
    setSearchCriteria(criteria);
  }, []);

  const performSearch = useCallback(
    async (criteria: string) => {
      if (!isMaximumNumberOfGroupMemberReached) {
        // TODO: Fix the any cast
        await dispatch(withAsyncThunkErrorHandling(() => onSearch(criteria) as any));
      }
      setUpdatingSearchCriteria(false);
    },
    [dispatch, isMaximumNumberOfGroupMemberReached, onSearch],
  );

  useEffect(() => {
    performSearch(debouncedSearchCriteria);
  }, [performSearch, debouncedSearchCriteria]);

  useEffect(() => {
    return () => updateSearchCriteria('');
  }, [updateSearchCriteria]);

  const addInviteeFromResult = useCallback(
    (result: T) => {
      setValue((prev) => [...prev, result]);
      updateSearchCriteria('');
    },
    [setValue, updateSearchCriteria],
  );

  const handleChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    updateSearchCriteria(e.target.value);
  };

  return (
    <div className="flex flex-col h-full">
      <div className="flex flex-row">
        <ChipSelect
          isLoading={searchStatus === 'Loading'}
          onChange={handleChange}
          optionsArray={actualSearchResults}
          placeholder={t('translation:SearchInviteePlaceholder')}
          isPlaceholderTransparent={!!value.length}
          selectedOptionIndex={0}
          selection={value}
          setSelectedOptionIndex={() => {}} // TODO: check if functionality needed
          setSelection={setValue}
          setValue={setSearchCriteria}
          value={searchCriteria}
        />
      </div>

      <hr />

      <div className="flex-1 overflow-auto">
        <div className="pt-3">
          <GroupMemberSelectorEmptyState
            searchCriteria={debouncedSearchCriteria}
            updatingSearchCriteria={searchStatus === 'Loading' || updatingSearchCriteria}
            selectionCount={value.length}
            resultCount={actualSearchResults.length}
            searchTerm={searchCriteria}
          />
          {actualSearchResults.length > 0 ? (
            <div className="space-y-0 divide-y">
              {actualSearchResults.map((item, index) => (
                <GroupMemberSelectorItem
                  key={item.id}
                  member={item}
                  onPress={() => addInviteeFromResult(item)}
                  disabledReason={disabledValues?.find((dv) => dv.id === item.id)?.reason ?? null}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};
